import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, TextField } from '@mui/material';
import { AppDialog } from '../ui';
import { Notifications } from '../../store/slices';
import { updateOrdersTime } from '../../api';
import { errorMessage } from '../../lib/errorMessage';

const DEFAULT_VALUES = {
    standardProductionTime: 3,
    expeditedProductionTime: 2,
};

interface Props {
    isPopUp: boolean;
    popUpHandler: (cancel?: boolean) => void;
    loadOrders: (params: any) => void;
    queryParams?: any;
    /** The values currently in effect; the form starts from them. */
    initialValues?: { standardProductionTime?: number | string; expeditedProductionTime?: number | string } | null;
}

type Field = keyof typeof DEFAULT_VALUES;

const FIELDS: { name: Field; label: string }[] = [
    { name: 'standardProductionTime', label: 'Standard production time' },
    { name: 'expeditedProductionTime', label: 'Expedited production time' },
];

/** "Update production time": POSTs both values and reloads the history. */
export default function OrdersProduction({ isPopUp, popUpHandler, loadOrders, queryParams, initialValues }: Props) {
    const dispatch = useDispatch();
    const [values, setValues] = useState<Record<Field, string>>({ standardProductionTime: '', expeditedProductionTime: '' });
    const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
    const [pending, setPending] = useState(false);
    const formId = React.useId();

    useEffect(() => {
        if (!isPopUp) return;
        setValues({
            standardProductionTime: String(initialValues?.standardProductionTime ?? DEFAULT_VALUES.standardProductionTime),
            expeditedProductionTime: String(initialValues?.expeditedProductionTime ?? DEFAULT_VALUES.expeditedProductionTime),
        });
        setErrors({});
    }, [isPopUp, initialValues]);

    const close = () => {
        if (!pending) popUpHandler(true);
    };

    const submit = async (event?: React.FormEvent) => {
        event?.preventDefault();
        if (pending) return;
        const parsed = {} as Record<Field, number>;
        const nextErrors: Partial<Record<Field, string>> = {};
        FIELDS.forEach(({ name }) => {
            const n = parseInt(values[name], 10);
            if (Number.isNaN(n) || n < 0) nextErrors[name] = 'Enter a whole number of days';
            parsed[name] = n;
        });
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;
        setPending(true);
        try {
            await updateOrdersTime({
                data: parsed,
                onSuccess: async () => {
                    dispatch(Notifications.setNotifications({ text: 'Production time updated', type: 'success' }));
                    loadOrders(queryParams);
                    popUpHandler(true);
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: errorMessage(error), type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    return (
        <AppDialog
            open={isPopUp}
            onClose={close}
            title="Update production time"
            description="Customers see the new values at checkout right away."
            pending={pending}
            maxWidth="xs"
            actions={(
                <>
                    <Button variant="text" onClick={close} disabled={pending}>Cancel</Button>
                    <Button variant="contained" type="submit" form={formId} disabled={pending}>Update</Button>
                </>
            )}
        >
            <form id={formId} onSubmit={submit} style={{ display: 'grid', gap: 16, paddingTop: 4 }}>
                {FIELDS.map(({ name, label }) => (
                    <TextField
                        key={name}
                        label={label}
                        type="number"
                        name={name}
                        value={values[name]}
                        required
                        inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                        error={!!errors[name]}
                        helperText={errors[name] || ' '}
                        onChange={(event) => {
                            setValues((prev) => ({ ...prev, [name]: event.target.value }));
                            setErrors((prev) => ({ ...prev, [name]: undefined }));
                        }}
                    />
                ))}
            </form>
        </AppDialog>
    );
}
